import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/client';
import { Notebook, Plus, Trash, Pencil, Users, CaretDown, CaretUp } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

function Batches() {
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const userServiceId = localStorage.getItem('user_service_id');
  const [formData, setFormData] = useState({
    name: '',
    age_range: '',
    description: '',
    service_id: '',
  });

  useEffect(() => {
    fetchBatches();
    fetchStudents();
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services', { params: { service_id: 'all' } });
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services');
    }
  };

  const serviceName = (id) => services.find((s) => s.id === id)?.name || '—';

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
    } catch (error) {
      toast.error('Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await api.get('/students');
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students');
    }
  };

  const getStudentCount = (batchId) => {
    return students.filter(s => s.batch_id === batchId && s.is_active).length;
  };

  const getBatchStudents = (batchId) => {
    return students
      .filter(s => s.batch_id === batchId && s.is_active)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  };

  const toggleExpand = (batchId) => {
    setExpandedBatch(expandedBatch === batchId ? null : batchId);
  };

  const getTotalStudents = () => {
    return students.filter(s => s.is_active).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userServiceId && !formData.service_id) {
      toast.error('Please select a service for this batch');
      return;
    }
    try {
      await api.post('/batches', formData);
      toast.success('Batch created successfully');
      setIsDialogOpen(false);
      setFormData({ name: '', age_range: '', description: '', service_id: '' });
      fetchBatches();
    } catch (error) {
      toast.error('Failed to create batch');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this batch?')) {
      try {
        await api.delete(`/batches/${id}`);
        toast.success('Batch deleted successfully');
        fetchBatches();
      } catch (error) {
        toast.error('Failed to delete batch');
      }
    }
  };

  const handleEdit = (batch) => {
    setSelectedBatch(batch);
    setFormData({
      name: batch.name,
      age_range: batch.age_range,
      description: batch.description || '',
      service_id: batch.service_id || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/batches/${selectedBatch.id}`, formData);
      toast.success('Batch updated successfully');
      setIsEditDialogOpen(false);
      setSelectedBatch(null);
      setFormData({ name: '', age_range: '', description: '', service_id: '' });
      fetchBatches();
    } catch (error) {
      toast.error('Failed to update batch');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 sm:p-8 lg:p-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-50 mb-2">
              Batches
            </h1>
            <p className="text-base text-slate-400">Manage your dance class batches</p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="add-batch-button"
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium px-6 py-3 rounded-md transition-colors"
              >
                <Plus size={20} weight="bold" />
                Add Batch
              </button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-slate-50">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-slate-50">Create New Batch</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                    Batch Name
                  </label>
                  <Input
                    data-testid="batch-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-slate-950 border-slate-800 text-slate-50"
                    placeholder="e.g., Junior Ballet"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                    Age Range
                  </label>
                  <Input
                    data-testid="batch-age-range-input"
                    value={formData.age_range}
                    onChange={(e) => setFormData({ ...formData, age_range: e.target.value })}
                    className="bg-slate-950 border-slate-800 text-slate-50"
                    placeholder="e.g., 5-8 years"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                    Description (Optional)
                  </label>
                  <Input
                    data-testid="batch-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-slate-950 border-slate-800 text-slate-50"
                    placeholder="Brief description"
                  />
                </div>
                {!userServiceId && (
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                      Service
                    </label>
                    <Select value={formData.service_id} onValueChange={(v) => setFormData({ ...formData, service_id: v })}>
                      <SelectTrigger data-testid="batch-service-select" className="bg-slate-950 border-slate-800 text-slate-50">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        {services.map((s) => (
                          <SelectItem key={s.id} value={s.id} className="text-slate-50">{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button
                  type="submit"
                  data-testid="submit-batch-button"
                  className="w-full bg-blue-600 hover:bg-blue-500"
                >
                  Create Batch
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {batches.length === 0 ? (
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
            <Notebook size={64} weight="duotone" className="mx-auto text-slate-700 mb-4" />
            <h3 className="text-xl font-semibold text-slate-300 mb-2">No batches yet</h3>
            <p className="text-slate-500 mb-6">Create your first batch to get started</p>
          </div>
        ) : (
          <>
            <div className="mb-6 bg-slate-900 border border-slate-800 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Users size={24} weight="duotone" className="text-blue-400" />
                <div>
                  <p className="text-sm text-slate-400">Total Students Across All Batches</p>
                  <p className="text-2xl font-bold text-slate-50">{getTotalStudents()}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {batches.map((batch) => (
                <div
                  key={batch.id}
                  data-testid={`batch-card-${batch.id}`}
                  className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden hover:border-slate-700 hover:shadow-lg hover:shadow-black/50 transition-all duration-200"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-600/20 flex items-center justify-center">
                        <Notebook size={24} weight="duotone" className="text-blue-400" />
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(batch)}
                          data-testid={`edit-batch-${batch.id}`}
                          className="p-2 hover:bg-slate-800 rounded-md transition-colors text-blue-400 hover:text-blue-300"
                          title="Edit Batch"
                        >
                          <Pencil size={18} weight="duotone" />
                        </button>
                        <button
                          onClick={() => handleDelete(batch.id)}
                          data-testid={`delete-batch-${batch.id}`}
                          className="p-2 hover:bg-slate-800 rounded-md transition-colors text-rose-400 hover:text-rose-300"
                          title="Delete Batch"
                        >
                          <Trash size={18} weight="duotone" />
                        </button>
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-slate-50 mb-2">{batch.name}</h3>
                    <p className="text-sm text-slate-400 mb-1">
                      <span className="text-blue-400 font-medium">Age Range:</span> {batch.age_range}
                    </p>
                    <p className="text-sm text-slate-400 mb-1">
                      <span className="text-blue-400 font-medium">Service:</span> {serviceName(batch.service_id)}
                    </p>
                    
                    <button
                      onClick={() => toggleExpand(batch.id)}
                      data-testid={`toggle-students-${batch.id}`}
                      className="w-full mt-3 p-3 bg-slate-950 border border-slate-800 rounded-md hover:border-slate-700 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-400">Students in batch:</span>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-emerald-400" data-testid={`student-count-${batch.id}`}>
                            {getStudentCount(batch.id)}
                          </span>
                          {expandedBatch === batch.id ? (
                            <CaretUp size={16} weight="bold" className="text-slate-400" />
                          ) : (
                            <CaretDown size={16} weight="bold" className="text-slate-400" />
                          )}
                        </div>
                      </div>
                    </button>

                    {batch.description && (
                      <p className="text-sm text-slate-500 mt-3">{batch.description}</p>
                    )}
                  </div>

                  {expandedBatch === batch.id && (
                    <div className="border-t border-slate-800 bg-slate-950 max-h-72 overflow-y-auto">
                      {getBatchStudents(batch.id).length === 0 ? (
                        <p className="px-6 py-4 text-sm text-slate-500 text-center">No students in this batch</p>
                      ) : (
                        <ul className="divide-y divide-slate-800/50">
                          {getBatchStudents(batch.id).map((student, idx) => (
                            <li
                              key={student.id}
                              data-testid={`batch-student-${student.id}`}
                              className="flex items-center gap-3 px-6 py-2.5 hover:bg-slate-900/50"
                            >
                              <span className="text-xs font-medium text-slate-500 w-6 text-right">{idx + 1}.</span>
                              {student.photo_url ? (
                                <img src={student.photo_url} alt={student.name} className="w-7 h-7 rounded-full object-cover border border-slate-700" />
                              ) : (
                                <div className="w-7 h-7 rounded-full bg-blue-600/20 flex items-center justify-center">
                                  <span className="text-xs font-bold text-blue-400">{student.name.charAt(0)}</span>
                                </div>
                              )}
                              <span className="text-sm text-slate-300">{student.name}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-50">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-50">Edit Batch</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                Batch Name
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-slate-950 border-slate-800 text-slate-50"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                Age Range
              </label>
              <Input
                value={formData.age_range}
                onChange={(e) => setFormData({ ...formData, age_range: e.target.value })}
                className="bg-slate-950 border-slate-800 text-slate-50"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                Description (Optional)
              </label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-slate-950 border-slate-800 text-slate-50"
              />
            </div>
            {!userServiceId && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                  Service
                </label>
                <Select value={formData.service_id} onValueChange={(v) => setFormData({ ...formData, service_id: v })}>
                  <SelectTrigger data-testid="edit-batch-service-select" className="bg-slate-950 border-slate-800 text-slate-50">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.id} className="text-slate-50">{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-500">
              Update Batch
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default Batches;
